// ─────────────────────────────────────────────────────────────────────────────
// Excel de «Reconocimiento Diario» — las 6 hojas del libro descargable, con la forma visual de la
// plantilla del cliente `01 - Reconocimiento Diario.xlsx` (auditoría 2026-09-05, medida celda a
// celda con openpyxl): banner teal de título + subtítulo, bloque de metadatos celeste, encabezado
// de tabla teal con «Ajustar texto», rejilla de bordes finos y anchos de columna suficientes para
// que ninguna celda salga apretada.
//
// Aquí viven solo las dos hojas PROPIAS de este reporte — «Resumen Ejecutivo» y «Definiciones» —:
// el sistema visual y las otras cuatro hojas salen de `excelPlantillaTeal.ts`, que este libro
// comparte con «Brillamos Juntos» porque las dos plantillas del cliente calcan el mismo diseño.
//
// EL LIBRO COMPLETO SALE EN EL IDIOMA DE QUIEN DESCARGA (`reports.daily.*`), en los 7 idiomas —
// mismo criterio ya aplicado en Líderes, Demográfico, Históricos y Brillamos. Un archivo que se
// comparte fuera de la app tiene que poder leerse sin saber español. Los rótulos de dato se reusan
// de las claves que la PANTALLA ya usa (`adoptionStaff`, `uniquePeople`, `colName`…) para que la
// celda del Excel y la columna de la tabla digan lo mismo; solo los textos exclusivos del archivo
// viven bajo `reports.daily.excel.*`.
//
// Diferencias A PROPÓSITO frente a la plantilla de referencia:
//   1. Sin la pestaña «Distribución Fundamental». El cliente pidió retirarla del libro (2026-09-05)
//      y su propia plantilla ya la traía marcada en rojo. El bloque sigue en pantalla.
//   2. Sin las tres filas en blanco que la plantilla reserva arriba del banner (`A1:A3` combinadas,
//      el hueco de su logotipo): este libro no inserta logotipo, y el hueco quedaría como una franja
//      vacía sin explicación.
//   3. Sin los pies «Datos ficticios demo generados para validar diseño y fórmulas» ni «Datos de
//      ejemplo del entorno demo»: describen el archivo de MUESTRA del cliente, no un reporte con
//      datos reales (mismo criterio ya aplicado en Líderes, Históricos y Moderación).
//   4. Sin los KPI «Total de reconocimientos a una sola persona» y «Total de reconocimientos
//      realizados a grupos». La plantilla los calcula con un `COUNTIF` sobre su tabla de muestra,
//      que trae una fila por reconocimiento; el export crudo real trae una fila por CANDIDATO y no
//      lleva identificador de reconocimiento, así que contar los grupales sobre él los inflaría.
//      El agregado del Diario (`DailyReportDto`) tampoco los trae — solo el de campaña. Publicar un
//      número inflado bajo ese rótulo sería peor que no publicarlo; la hoja «Todos los
//      reconocimientos» sí conserva la columna «Recognition type» para quien necesite el detalle.
//   5. Los KPI «Periodo anterior», «Variación» y «Personas únicas» no están en la plantilla — los
//      pide la spec de Reportería. Van al FINAL de la tabla para que las filas que la plantilla sí
//      trae salgan exactamente en su orden.
//   6. El periodo del Embudo va en el bloque de metadatos, no como fila de KPI (es un filtro, no un
//      indicador, y como texto dentro de la columna «Valor» rompía la alineación). Desde el
//      2026-10-20 es el MISMO periodo de arriba (minuta de revisión del Reco Diario); la fila se
//      conserva porque la plantilla del cliente la trae y deja explícito el rango que usó el servidor.
// ─────────────────────────────────────────────────────────────────────────────
import { mergeDeFila } from '@/utils/exportExcelMultiSheet';
import type { ExcelCellStyleHint, ExcelCellValue, ExcelSheetSpec } from '@/utils/exportExcelMultiSheet';
import { excelColors } from '@/utils/excelTheme';
import {
  CENTRADO_VERTICAL, FORMATO_ENTERO, FORMATO_PORCENTAJE, TEMA_PLANTILLA_TEAL, columnaDeTexto,
  estilosDeBanner, estilosDeMetadatos, rejillaDeTabla,
} from './excelPlantillaTeal';
import type { FilaDefinicionGlosario, Traductor } from './excelPlantillaTeal';

/**
 * Variación contra el periodo anterior: fracción 0-1 con el signo SIEMPRE visible, para que un
 * `-0.12` se lea «-12.0 %» y un `+0.12` no se confunda con un valor absoluto.
 */
const FORMATO_VARIACION = '+0.0%;-0.0%;0.0%';

/** Columnas del Resumen Ejecutivo: KPI · Valor · Detalle, más la cuarta del bloque de plantilla. */
const TOTAL_COLUMNAS_RESUMEN = 4;
/** Hasta dónde llega el banner y el bloque de metadatos, tal como los extiende la plantilla. */
const ANCHO_BANNER_RESUMEN = 6;
const TOTAL_COLUMNAS_DEFINICIONES = 3;

/** Anchos medidos de la plantilla (columnas A-D de «Resumen Ejecutivo»). */
const ANCHOS_RESUMEN = [45.66, 18.11, 43.89, 18.11];
/** Anchos medidos de la plantilla (columnas A-C de «Definiciones»). */
const ANCHOS_DEFINICIONES = [40, 47.55, 53.33];

/** Filas 0-indexed del bloque «Periodo / Filtros aplicados / Periodo del embudo / Generado». */
const FILAS_META_RESUMEN = [3, 4, 5, 6];
/** Fila 0-indexed del encabezado «KPI | Valor | Detalle» (banner + 1 en blanco + meta + 1 en blanco). */
const FILA_HEADER_RESUMEN = 8;
/** Fila 0-indexed del encabezado «Dato | Qué es | Cómo se calcula» (banner + 2 en blanco). */
const FILA_HEADER_DEFINICIONES = 4;

/**
 * KPIs del Resumen Ejecutivo, EN EL ORDEN EN QUE SE PINTAN. Los conteos viajan como NÚMERO (o como
 * el guion de «sin dato» cuando el agregado está suprimido o el censo no respondió), nunca como
 * texto ya formateado: un `"77.9%"` string se alinea a la izquierda mientras los conteos numéricos
 * se van a la derecha, y la columna «Valor» sale con los datos en dos alineaciones distintas — el
 * defecto que este rediseño corrige. Así, además, el número sigue siendo sumable y ordenable.
 *
 * Los porcentajes viajan como FRACCIÓN 0-1, no como el número ya multiplicado por cien: el formato
 * nativo (`0.00%`) hace la conversión al pintar.
 */
export interface KpisDiario {
  pares: ExcelCellValue;
  lideres: ExcelCellValue;
  adopcionStaff: ExcelCellValue;
  detalleAdopcionStaff: string;
  adopcionFrontline: ExcelCellValue;
  detalleAdopcionFrontline: string;
  usuariosTotales: ExcelCellValue;
  detalleUsuariosTotales: string;
  sinActividad: ExcelCellValue;
  reconocimientosEnviados: ExcelCellValue;
  periodoAnterior: ExcelCellValue;
  variacion: ExcelCellValue;
  personasUnicas: ExcelCellValue;
  /** 2026-10-26 — «Reconocimientos grupales»: de los enviados, los dirigidos a 2 o más personas. */
  reconocimientosGrupales: ExcelCellValue;
  plantillaTotal: ExcelCellValue;
  plantillaStaff: ExcelCellValue;
  plantillaFrontline: ExcelCellValue;
}

/** Metadatos del banner: los renglones celestes que la plantilla pone sobre la tabla de KPIs. */
export interface MetadatosDiario {
  periodo: string;
  filtrosAplicados: string;
  /** Rango que usó el servidor para el Embudo de Adopción: el mismo periodo de arriba (2026-10-20). */
  mesDelEmbudo: string;
  generado: string;
}

/**
 * Hoja «Resumen Ejecutivo». El orden de los KPI es el de la plantilla y NO debe reordenarse: Pares,
 * Líderes, Usuarios Staff, Usuarios Frontline, Usuarios totales, Sin actividad y Total de
 * reconocimientos enviados. Los tres KPI que la spec agrega y la plantilla no trae cierran la tabla
 * (nota 5 del encabezado del archivo), y el bloque «Plantilla | Total | Staff | Frontline» la
 * remata igual que en la plantilla.
 */
export function buildResumenEjecutivoSheet(
  traducir: Traductor, metadatos: MetadatosDiario, kpis: KpisDiario,
): ExcelSheetSpec {
  const banner = estilosDeBanner(ANCHO_BANNER_RESUMEN);

  const filasKpi: Array<{ label: string; valor: ExcelCellValue; detalle: string; formato: string }> = [
    {
      label: traducir('excel.kpiPeers'),
      valor: kpis.pares,
      detalle: traducir('excel.whatPeers'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('excel.kpiLeaders'),
      valor: kpis.lideres,
      detalle: traducir('excel.whatLeaders'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('adoptionStaff'),
      valor: kpis.adopcionStaff,
      detalle: kpis.detalleAdopcionStaff,
      formato: FORMATO_PORCENTAJE,
    },
    {
      label: traducir('adoptionFrontline'),
      valor: kpis.adopcionFrontline,
      detalle: kpis.detalleAdopcionFrontline,
      formato: FORMATO_PORCENTAJE,
    },
    {
      label: traducir('adoptionTotal'),
      valor: kpis.usuariosTotales,
      detalle: kpis.detalleUsuariosTotales,
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('noActivity'),
      valor: kpis.sinActividad,
      detalle: traducir('excel.whatNoActivity'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('totalSentTitle'),
      valor: kpis.reconocimientosEnviados,
      detalle: traducir('excel.whatRecognitionsMade'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('prevPeriod'),
      valor: kpis.periodoAnterior,
      detalle: traducir('excel.whatPrevPeriod'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('variation'),
      valor: kpis.variacion,
      detalle: traducir('excel.whatVariation'),
      formato: FORMATO_VARIACION,
    },
    {
      label: traducir('uniquePeople'),
      valor: kpis.personasUnicas,
      detalle: traducir('excel.whatUniqueRecognized'),
      formato: FORMATO_ENTERO,
    },
    // 2026-10-26 — al final, como los otros KPI que la plantilla no trae: el orden de arriba no se toca.
    {
      label: traducir('groupRecognitions'),
      valor: kpis.reconocimientosGrupales,
      detalle: traducir('excel.whatGroupRecognitions'),
      formato: FORMATO_ENTERO,
    },
  ];

  const primeraFilaKpi = FILA_HEADER_RESUMEN + 1;
  const ultimaFilaKpi = primeraFilaKpi + filasKpi.length - 1;
  const filaHeaderPlantilla = ultimaFilaKpi + 2;
  const filaPlantilla = filaHeaderPlantilla + 1;

  const rows: ExcelCellValue[][] = [
    [traducir('excel.summaryTitle')],
    [traducir('excel.summarySubtitle')],
    [],
    [traducir('excel.metaPeriod'), metadatos.periodo],
    [traducir('excel.metaFilters'), metadatos.filtrosAplicados],
    [traducir('excel.metaFunnelMonth'), metadatos.mesDelEmbudo],
    [traducir('excel.metaGenerated'), metadatos.generado],
    [],
    [traducir('excel.colKpi'), traducir('excel.colValue'), traducir('excel.colDetail')],
    ...filasKpi.map((kpi): ExcelCellValue[] => [kpi.label, kpi.valor, kpi.detalle]),
    [],
    [
      traducir('excel.headcountTitle'), traducir('excel.headcountTotal'),
      traducir('excel.headcountStaff'), traducir('excel.headcountFrontline'),
    ],
    [
      traducir('excel.headcountPeople'), kpis.plantillaTotal, kpis.plantillaStaff, kpis.plantillaFrontline,
    ],
  ];

  return {
    name: traducir('excel.sheetSummary'),
    rows,
    styleHints: {
      theme: TEMA_PLANTILLA_TEAL,
      headerRows: [FILA_HEADER_RESUMEN, filaHeaderPlantilla],
      columnWidths: ANCHOS_RESUMEN,
      rowHeights: banner.rowHeights,
      merges: [
        ...banner.merges,
        ...FILAS_META_RESUMEN.map((fila) => mergeDeFila(fila, 1, ANCHO_BANNER_RESUMEN - 1)),
      ],
      dataColumnStyles: {
        startRow: primeraFilaKpi,
        endRow: ultimaFilaKpi,
        columns: [
          { ...columnaDeTexto(0), fillArgb: excelColors.filaPar },
          {
            column: 1,
            fillArgb: excelColors.filaPar,
            fontColorArgb: excelColors.valorDestacado,
            isBold: true,
            // La alineación se fija para TODA la columna, no por celda: mezcla números y el guion
            // de «sin dato», y sin esto Excel manda el número a la derecha y el guion a la
            // izquierda — la columna «Valor» salía con los datos en dos alineaciones distintas.
            horizontal: 'right',
            vertical: CENTRADO_VERTICAL,
          },
          {
            ...columnaDeTexto(2),
            fillArgb: excelColors.filaPar,
            fontColorArgb: excelColors.textoDetalle,
          },
        ],
      },
      cellStyles: [
        ...banner.cellStyles,
        ...estilosDeMetadatos(FILAS_META_RESUMEN),
        // Un formato numérico por FILA: la columna «Valor» mezcla conteos, porcentajes de adopción
        // y la variación con signo, así que no puede fijarse a nivel de columna.
        ...filasKpi.map((kpi, indice): ExcelCellStyleHint => (
          { row: primeraFilaKpi + indice, column: 1, numFmt: kpi.formato }
        )),
        // Bloque «Plantilla»: la etiqueta a la izquierda y las tres cifras alineadas a la derecha,
        // igual que la columna «Valor» de arriba.
        { row: filaPlantilla, column: 0, horizontal: 'left', vertical: CENTRADO_VERTICAL },
        ...[1, 2, 3].map((columna): ExcelCellStyleHint => ({
          row: filaPlantilla,
          column: columna,
          fontColorArgb: excelColors.valorDestacado,
          isBold: true,
          horizontal: 'right',
          vertical: CENTRADO_VERTICAL,
          numFmt: FORMATO_ENTERO,
        })),
      ],
      borderedRanges: [
        rejillaDeTabla(FILAS_META_RESUMEN[0], FILAS_META_RESUMEN.at(-1)!, ANCHO_BANNER_RESUMEN - 1),
        rejillaDeTabla(FILA_HEADER_RESUMEN, ultimaFilaKpi, TOTAL_COLUMNAS_RESUMEN - 2),
        rejillaDeTabla(filaHeaderPlantilla, filaPlantilla, TOTAL_COLUMNAS_RESUMEN - 1),
      ],
    },
  };
}

/**
 * Las once definiciones que trae la plantilla, EN SU ORDEN, más las cuatro que documentan las hojas
 * de detalle y los KPI que este libro sí exporta y la plantilla no explicaba. Función de módulo
 * para que `buildDefinicionesSheet` se quede solo con el armado de la hoja.
 */
function definicionesDeLaPlantilla(traducir: Traductor): FilaDefinicionGlosario[] {
  return [
    {
      dato: traducir('totalSentTitle'),
      queEs: traducir('excel.whatRecognitionsMade'),
      comoSeCalcula: traducir('excel.calcRecognitionsMade'),
    },
    {
      dato: traducir('uniquePeople'),
      queEs: traducir('excel.whatUniqueRecognized'),
      comoSeCalcula: traducir('excel.calcUniqueRecognized'),
    },
    // 2026-10-26 — la fila de grupales de la plantilla es ahora la del KPI «Reconocimientos grupales».
    {
      dato: traducir('groupRecognitions'),
      queEs: traducir('excel.whatGroupRecognitions'),
      comoSeCalcula: traducir('excel.calcGroupRecognitions'),
    },
    {
      dato: traducir('excel.kpiPeers'),
      queEs: traducir('excel.whatPeers'),
      comoSeCalcula: traducir('excel.calcPeers'),
    },
    {
      dato: traducir('excel.kpiLeaders'),
      queEs: traducir('excel.whatLeaders'),
      comoSeCalcula: traducir('excel.calcLeaders'),
    },
    {
      dato: traducir('adoptionStaff'),
      queEs: traducir('excel.whatStaffAdoption'),
      comoSeCalcula: traducir('excel.calcStaffAdoption'),
    },
    {
      dato: traducir('adoptionFrontline'),
      queEs: traducir('excel.whatFrontlineAdoption'),
      comoSeCalcula: traducir('excel.calcFrontlineAdoption'),
    },
    {
      dato: traducir('adoptionTotal'),
      queEs: traducir('excel.whatTotalUsers'),
      comoSeCalcula: traducir('excel.calcTotalUsers'),
    },
    // Las dos filas de audiencia de la plantilla explican QUÉ es cada segmento; no tienen columna
    // «Cómo se calcula» porque no son un cálculo, sino la definición del padrón.
    {
      dato: traducir('excel.datumAudienceStaff'),
      queEs: traducir('excel.whatAudienceStaff'),
      comoSeCalcula: '',
    },
    {
      dato: traducir('excel.datumAudienceFrontline'),
      queEs: traducir('excel.whatAudienceFrontline'),
      comoSeCalcula: '',
    },
    {
      dato: traducir('noActivity'),
      queEs: traducir('excel.whatNoActivity'),
      comoSeCalcula: traducir('excel.calcNoActivity'),
    },
    {
      dato: traducir('excel.datumComparison'),
      queEs: traducir('excel.whatVariation'),
      comoSeCalcula: traducir('excel.calcVariation'),
    },
    {
      dato: traducir('excel.sheetFunnel'),
      queEs: traducir('excel.whatFunnel'),
      comoSeCalcula: traducir('excel.calcFunnel'),
    },
    {
      dato: traducir('excel.datumRankings'),
      queEs: traducir('excel.whatRankings'),
      comoSeCalcula: traducir('excel.calcRankings'),
    },
    {
      dato: traducir('excel.sheetAllRecognitions'),
      queEs: traducir('excel.whatAllRecognitions'),
      comoSeCalcula: traducir('excel.calcAllRecognitions'),
    },
    {
      dato: traducir('excel.sheetReceived'),
      queEs: traducir('excel.whatReceived'),
      comoSeCalcula: traducir('excel.calcReceived'),
    },
  ];
}

/**
 * Hoja «Definiciones» — el glosario «Dato · Qué es · Cómo se calcula» de la plantilla, con sus once
 * filas en el mismo orden, las cuatro que este libro agrega para lo que sí exporta y el recuadro
 * crema que aclara cuándo un rol cuenta como líder.
 *
 * 2026-10-19 — ya no lleva la fila de «archivo incompleto»: «Todos los reconocimientos» no tiene tope
 * de filas, así que el libro nunca se corta.
 */
export function buildDefinicionesSheet(traducir: Traductor): ExcelSheetSpec {
  const banner = estilosDeBanner(TOTAL_COLUMNAS_DEFINICIONES);
  const definiciones = definicionesDeLaPlantilla(traducir);

  const primeraFilaDato = FILA_HEADER_DEFINICIONES + 1;
  const ultimaFilaDato = primeraFilaDato + definiciones.length - 1;
  const filaNotaLider = ultimaFilaDato + 2;

  const rows: ExcelCellValue[][] = [
    [traducir('excel.definitionsTitle')],
    [traducir('excel.definitionsSubtitle')],
    [],
    [],
    [traducir('excel.colDatum'), traducir('excel.colWhatIs'), traducir('excel.colHowCalculated')],
    ...definiciones.map((definicion): ExcelCellValue[] => (
      [definicion.dato, definicion.queEs, definicion.comoSeCalcula]
    )),
    [],
    [traducir('excel.datumLeader'), traducir('excel.noteLeader'), ''],
  ];

  return {
    name: traducir('excel.sheetDefinitions'),
    rows,
    styleHints: {
      theme: TEMA_PLANTILLA_TEAL,
      headerRows: [FILA_HEADER_DEFINICIONES],
      columnWidths: ANCHOS_DEFINICIONES,
      rowHeights: banner.rowHeights,
      merges: banner.merges,
      dataColumnStyles: {
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        // Sin `fillArgb`: el banding zebra blanco/celeste del tema ya pinta estas filas, igual que
        // en la plantilla, y fijar un color aquí lo borraría.
        columns: [0, 1, 2].map((columna) => ({ ...columnaDeTexto(columna), vertical: 'top' as const })),
      },
      cellStyles: [
        ...banner.cellStyles,
        // Recuadro crema del pie: la plantilla lo usa para la aclaración de qué cuenta como líder.
        ...[0, 1, 2].map((columna): ExcelCellStyleHint => ({
          row: filaNotaLider,
          column: columna,
          fillArgb: excelColors.notaFondo,
          fontColorArgb: excelColors.notaTexto,
          isBold: columna === 0,
          hasWrapText: true,
          horizontal: 'left',
          vertical: CENTRADO_VERTICAL,
        })),
      ],
      borderedRanges: [
        rejillaDeTabla(FILA_HEADER_DEFINICIONES, ultimaFilaDato, TOTAL_COLUMNAS_DEFINICIONES - 1),
        rejillaDeTabla(filaNotaLider, filaNotaLider, TOTAL_COLUMNAS_DEFINICIONES - 1),
      ],
    },
  };
}
